<template>
  <div class="layout">
    <div class="header">
      <img src="/logo.png" :height="30" />
    </div>
    <div class="pre-call-content">
      <v-sheet :rounded="8" :border="true" :max-width="maxWidth">
        <v-row :gap="0" no-gutters>
          <v-col :cols="12" class="pre-call-avatar">
            <template v-if="initialState.isCameraRecommended">
              <video ref="video" autoplay playsinline muted></video>
              <div class="video-placeholder" v-if="!camOn">
                <a-icon name="camera-off" :width="80" :height="80" color="#949494" />
              </div>
            </template>
            <div v-else class="avatar-placeholder">
              <v-avatar :size="130">
                <v-img v-if="session.info?.user?.avatarUrl" :src="session.info.user.avatarUrl" cover></v-img>
                <v-icon v-else size="80" icon="msf:headset_mic" />
              </v-avatar>
            </div>
          </v-col>
        </v-row>
        <v-row class="pre-call-inputs mt-5" no-gutters>
          <v-col :cols="12">
            <pre-call-input />
          </v-col>
        </v-row>
        <v-row class="pre-call-inputs" no-gutters>
          <v-col cols="12" :md="initialState.isCameraRecommended ? 7 : 6" order="3" order-md="1" class="mt-5">
            <pre-call-media />
          </v-col>
          <v-col
            cols="12"
            :md="initialState.isCameraRecommended ? 5 : 6"
            order="1"
            order-md="2"
            class="mt-5"
            v-if="mobile || joinMode === 'key' || isJoining"
          >
            <v-btn
              size="large"
              prepend-icon="msf:call"
              :color="!mobile && joinMode === 'pin' ? 'default' : 'primary'"
              :variant="!mobile && joinMode === 'pin' ? 'plain' : 'elevated'"
              block
              :loading="isJoining"
              :disabled="isJoinButtonDisabled"
              @click="joinSession()"
            >
              {{ $t('preCall.join.action.joinSession') }}
              <template #loader>
                <div class="pre-call-join-loading">
                  <loading-spinner />
                  <span class="joining-status__text">{{ $t('preCall.join.action.joiningSession') }}</span>
                </div>
              </template>
            </v-btn>
          </v-col>
          <v-col class="mt-5" cols="12" order="2" order-md="3" v-if="sessionErrorMsg">
            <pre-call-alert :alert="sessionErrorMsg" color="#F8DDDD" icon-color="#D83737" />
          </v-col>
          <v-col class="mt-5" cols="12" order="2" order-md="3" v-if="isClientWaiting">
            <pre-call-alert
              :alert="$t('preCall.alert.clientWaiting', 'Your client is waiting for you to join.')"
              color="#FFEDD4"
              icon-color="#9D5D00"
            />
          </v-col>
          <v-col class="mt-5" cols="12" order="2" order-md="3" v-if="slowNetwork">
            <pre-call-alert :alert="$t('preCall.alert.subTitlePoorNetwork1')" color="#FFEDD4" icon-color="#9D5D00" />
          </v-col>
        </v-row>
        <v-row no-gutters class="mt-5">
          <v-expand-transition>
            <v-col :cols="12" class="device-menu" v-if="openedPanel !== null">
              <meeting-device-menu
                :microphone="openedPanel === 'settings' || openedPanel === 'microphone'"
                :speaker="openedPanel === 'settings' || openedPanel === 'speaker'"
              />
            </v-col>
          </v-expand-transition>
        </v-row>
      </v-sheet>
      <modal-permissions
        v-if="permissionsModalDevice"
        :device="permissionsModalDevice"
        @close="permissionsModalDevice = null"
        @retry-and-close="retryStream()"
      />
    </div>
    <div class="footer">
      <span class="term-of-use" v-html="$t('preCall.join.agreement')"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useLocationStore, useSessionStore} from '@/stores'
import {useWearML} from '@/composables'
import {useWaitingParty} from '@/composables/useWaitingParty'
import {usePreCall} from '@/stores/preCall'
import {storeToRefs} from 'pinia'
import {useStreamStore} from '@/stores/streamStore'
import {useDisplay} from 'vuetify'
import {computed} from 'vue'

const initialState = window.__INITIAL_STATE__.app
useLocationStore()
const {platform, xs, name} = useDisplay()
const mobile = computed(() => (platform.value.android && platform.value.ios) || xs.value)
const maxWidth = computed(() => {
  switch (name.value) {
    case 'xs':
    case 'sm':
      return 328
    case 'md':
    case 'lg':
    case 'xl':
    case 'xxl':
      return initialState.isCameraRecommended ? 430 : 400
  }
})
const session = useSessionStore()
const wearML = useWearML()
const preCall = usePreCall()
const video = ref()
const {
  joinMode,
  validInput,
  isJoining,
  slowNetwork,
  openedPanel,
  permissionsModalDevice,
  sessionErrorMsg,
  isJoinButtonDisabled,
} = storeToRefs(preCall)
const {validateSession, joinSession} = preCall
const {isClientWaiting, start, stop} = useWaitingParty()
const streamStore = useStreamStore()
const {camOn, camStream, cameraAccess, microphoneAccess, camEnabled, micEnabled} = storeToRefs(streamStore)
const {restartCam, restartMic} = streamStore

const retryStream = async () => {
  if (permissionsModalDevice.value === 'camera') {
    if (cameraAccess.value !== 'denied') {
      await restartCam()
    }
    camEnabled.value = true
  } else if (permissionsModalDevice.value === 'microphone') {
    if (microphoneAccess.value !== 'denied') {
      await restartMic()
    }
    micEnabled.value = true
  }
  permissionsModalDevice.value = null
}

watch(camStream, stream => {
  if (video?.value) {
    if (video.value.srcObject !== stream) {
      video.value.srcObject = stream
    }
    video.value.play().catch(() => {})
  }
})

onMounted(async () => {
  streamStore.connectAndroid()
  wearML.refresh()
  if (validInput.value) {
    await validateSession()
    start()
  }
})
onBeforeUnmount(stop)
</script>
<style scoped lang="scss">
@import '@/scss/layout';
</style>
<style scoped lang="scss">
$card-border-radius: 8px;
.pre-call-content {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  .pre-call-inputs {
    padding-left: $spacing-m;
    padding-right: $spacing-m;
    justify-content: center;
    align-items: center;
  }

  .pre-call-avatar {
    border-top-left-radius: $card-border-radius;
    border-top-right-radius: $card-border-radius;
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }

    .video-placeholder {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #eeeeee;
    }
    .avatar-placeholder {
      background-color: #eeeeee;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
.device-menu {
  border-top: 1px solid #c8c8c8;
  background: #fafafa;
  border-bottom-left-radius: $card-border-radius;
  border-bottom-right-radius: $card-border-radius;
}
.pre-call-join-loading {
  display: flex;
  gap: 0.8em;
}
</style>
